import { useEffect, useState } from 'react'
import GlassOverlay from '../GlassOverlay'
import { formatCoordinates, requestCurrentPosition } from '../../services/scan/geolocation'
import { buildMapsUrl, copyText } from '../../services/scan/mapsLink'
import { readItemStore } from '../../utils/itemMeta'
import { IconMore, IconPin, IconRoute } from './ScanIcons'
import ScanMenu from './ScanMenu'
import './ScanProductFlow.css'

export default function ProductPlaceSheet({ item, isOpen, onClose, onUpdate, returnFocusRef }) {
  const store = readItemStore(item)
  const [copied, setCopied] = useState(false)
  const [editing, setEditing] = useState(false)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')
  const [name, setName] = useState('')
  const [address, setAddress] = useState('')
  const [coords, setCoords] = useState(null)

  useEffect(() => {
    setName(store?.name || '')
    setAddress(store?.address || '')
    setCoords(store?.lat != null && store?.lng != null ? {
      lat: store.lat,
      lng: store.lng,
      accuracy: store.accuracy,
    } : null)
    setEditing(false)
    setCopied(false)
    setError('')
  }, [item?.id, store?.name, store?.address, store?.lat, store?.lng, store?.accuracy])

  if (!store) return null

  const mapsUrl = buildMapsUrl({
    name: editing ? name : store.name,
    address: editing ? address : store.address,
    lat: coords?.lat ?? store.lat,
    lng: coords?.lng ?? store.lng,
  })
  const coordsLabel = formatCoordinates(coords || store)
  const copyValue = store.address || coordsLabel || store.name

  const replacePosition = async () => {
    if (saving) return
    setSaving(true)
    setError('')
    try {
      const position = await requestCurrentPosition()
      setCoords({
        lat: position.lat,
        lng: position.lng,
        accuracy: position.accuracy,
        recordedAt: position.recordedAt || new Date().toISOString(),
      })
    } catch (nextError) {
      const denied = String(nextError?.message || '').includes('denied')
      setError(denied ? 'Localisation refusée.' : 'Position introuvable.')
    } finally {
      setSaving(false)
    }
  }

  const savePlace = () => {
    onUpdate({
      ...store,
      name: name.trim(),
      address: address.trim(),
      lat: coords?.lat ?? store.lat ?? null,
      lng: coords?.lng ?? store.lng ?? null,
      accuracy: coords?.accuracy ?? store.accuracy ?? null,
      recordedAt: coords?.recordedAt || (coords?.lat != null ? new Date().toISOString() : store.recordedAt),
      provider: coords?.lat != null ? 'gps' : store.provider,
      mode: store.placeId ? 'confirmed' : 'coords-only',
    })
  }

  return (
    <GlassOverlay
      isOpen={isOpen}
      onClose={onClose}
      returnFocusRef={returnFocusRef}
      labelledBy="scan-place-title"
      panelClassName="scan-place-sheet"
      followPointer={false}
    >
      <div className="scan-place-sheet">
        <header className="scan-place-header">
          <div>
            <h2 id="scan-place-title">{store.name || 'Emplacement enregistré'}</h2>
            {store.address ? <p className="scan-place-copy">{store.address}</p> : null}
            {coordsLabel ? <p className="scan-place-meta">{coordsLabel}</p> : null}
          </div>
          <button type="button" className="scan-icon-btn" onClick={onClose} aria-label="Fermer">✕</button>
        </header>
        {editing ? (
          <div className="scan-place-edit">
            <div className="scan-store-search">
              <input
                value={name}
                placeholder="Nom du magasin"
                onChange={(event) => setName(event.target.value)}
                aria-label="Nom du magasin"
              />
              <button
                type="button"
                className="scan-store-geo"
                onClick={replacePosition}
                disabled={saving}
                aria-label="Utiliser ma position"
                aria-busy={saving || undefined}
              >
                <IconPin />
              </button>
            </div>
            <textarea
              rows={2}
              value={address}
              placeholder="Adresse complète"
              onChange={(event) => setAddress(event.target.value)}
              aria-label="Adresse"
            />
            {coords ? <p className="scan-place-meta">{formatCoordinates(coords)}</p> : null}
            {error ? <p className="scan-product-hint" role="status">{error}</p> : null}
            <div className="scan-place-actions">
              <button type="button" className="scan-ghost" onClick={() => setEditing(false)}>Annuler</button>
              <button type="button" className="scan-primary" onClick={savePlace}>Enregistrer</button>
            </div>
          </div>
        ) : (
          <div className="scan-place-actions">
            {mapsUrl ? (
              <a className="scan-primary scan-place-route" href={mapsUrl} target="_blank" rel="noopener noreferrer">
                <IconRoute />
                <span>Itinéraire</span>
              </a>
            ) : null}
            <ScanMenu
              ariaLabel="Autres actions d’emplacement"
              label={<IconMore />}
              triggerClassName="scan-icon-btn"
              align="end"
              items={[
                {
                  label: copied
                    ? (store.address ? 'Adresse copiée' : 'Coordonnées copiées')
                    : (store.address ? 'Copier l’adresse' : 'Copier les coordonnées'),
                  onClick: async () => {
                    const ok = await copyText(copyValue)
                    setCopied(ok)
                  },
                },
                {
                  label: 'Modifier l’emplacement',
                  onClick: () => setEditing(true),
                },
                {
                  label: 'Retirer l’emplacement',
                  onClick: () => onUpdate(null),
                },
              ]}
            />
          </div>
        )}
      </div>
    </GlassOverlay>
  )
}
